import PropTypes from 'prop-types';
import React, { Component } from 'react';

export default class WebScrollView extends Component {
  containerRef = React.createRef();
  scrollTimeoutRef = null;

  setContainerRef = (element) => {
    this.containerRef.current = element;
    const { scrollContainerRef } = this.props;
    if (scrollContainerRef && typeof scrollContainerRef === 'object') {
      scrollContainerRef.current = element;
    }
  };

  renderItem = (item, index) => {
    const { renderItem } = this.props;
    return renderItem({ item, index });
  };

  componentDidMount() {
    this.scrollToBottom();
  }

  componentWillUnmount() {
    if (this.scrollTimeoutRef) {
      clearTimeout(this.scrollTimeoutRef);
    }
    if (this.props.scrollContainerRef && typeof this.props.scrollContainerRef === 'object') {
      this.props.scrollContainerRef.current = null;
    }
  }

  handleScroll = (e) => {
    if (this.containerRef.current) {
      this.containerRef.current.classList.add('scrolling');

      if (this.scrollTimeoutRef) {
        clearTimeout(this.scrollTimeoutRef);
      }

      this.scrollTimeoutRef = setTimeout(() => {
        if (this.containerRef.current) {
          this.containerRef.current.classList.remove('scrolling');
        }
      }, 800);
    }

    if (this.props.onScroll) {
      this.props.onScroll(e);
    }
  };

  getSnapshotBeforeUpdate(prevProps) {
    // 在更新前，如果数据长度增加且当前接近顶部，保存滚动状态
    // 如果之前的数据为空（切换群组后的初始加载），不保存滚动状态，应滚动到底部
    if (this.props.data.length > prevProps.data.length && prevProps.data.length > 0 && this.containerRef.current) {
      const container = this.containerRef.current;
      const scrollTop = container.scrollTop;
      const scrollHeight = container.scrollHeight;

      // 如果接近顶部（可能是加载历史消息），保存当前滚动状态
      const isNearTop = scrollTop < 200; // 稍微扩大判断范围，确保能捕获到加载历史消息的场景

      if (isNearTop) {
        return {
          scrollTop: scrollTop,
          scrollHeight: scrollHeight,
          shouldPreserveScroll: true
        };
      }
    }
    return null;
  }

  componentDidUpdate(prevProps, prevState, snapshot) {
    // 只有当数据长度增加且 shouldAutoScrollToBottom 为 true 时才自动滚动到底部
    // 如果用户在顶部加载历史消息（scrollTop 接近 0），则不自动滚动，并保持滚动位置
    if (this.props.data.length > prevProps.data.length) {
      const shouldAutoScroll = this.props.shouldAutoScrollToBottom !== false;

      if (this.containerRef.current) {
        const container = this.containerRef.current;

        // 如果 snapshot 表明需要保持滚动位置（加载历史消息场景）
        if (snapshot && snapshot.shouldPreserveScroll) {
          const oldScrollHeight = snapshot.scrollHeight;
          const oldScrollTop = snapshot.scrollTop;

          // 使用双重 requestAnimationFrame 确保 DOM 完全渲染完成
          requestAnimationFrame(() => {
            requestAnimationFrame(() => {
              if (container) {
                const newScrollHeight = container.scrollHeight;
                const heightDiff = newScrollHeight - oldScrollHeight;

                // 调整滚动位置，使之前看到的内容保持在相同位置
                // 新消息被添加到顶部，所以需要增加 scrollTop 来补偿高度差
                if (heightDiff > 0) {
                  container.scrollTop = oldScrollTop + heightDiff;
                }
              }
            });
          });
          return;
        }

        // 检查是否需要自动滚动到底部（新消息场景）
        if (shouldAutoScroll) {
          const scrollTop = container.scrollTop;
          const scrollHeight = container.scrollHeight;
          const clientHeight = container.clientHeight;

          // 计算距离底部的距离
          const distanceFromBottom = scrollHeight - scrollTop - clientHeight;
          // 如果滚动位置接近顶部（距离顶部小于 100px），说明是在加载历史消息，不自动滚动
          const isNearTop = scrollTop < 100;
          // 如果用户在底部附近（距离底部小于 150px），说明是新消息，应该自动滚动到底部
          // 使用 150px 的阈值，比之前的 50px 更宽松，确保在底部附近都能自动滚动
          const isNearBottom = distanceFromBottom < 150;

          // 初始加载（切换群组后数据从空变为有内容）时始终滚动到底部
          // 其他情况只有在接近底部且不接近顶部时才自动滚动（说明是新消息）
          if (prevProps.data.length === 0 || (isNearBottom && !isNearTop)) {
            // 使用双重 requestAnimationFrame 确保 DOM 完全渲染后再滚动
            requestAnimationFrame(() => {
              requestAnimationFrame(() => {
                this.scrollToBottom();
              });
            });
          }
        }
      }
    }
  }

  scrollToBottom = () => {
    if (this.containerRef.current) {
      const container = this.containerRef.current;
      // 确保滚动到底部，使用 scrollHeight - clientHeight 来精确计算
      const scrollTop = container.scrollHeight - container.clientHeight;
      requestAnimationFrame(() => {
        container.scrollTop = scrollTop;
        // 如果因为某些原因没有滚动到底部，再试一次
        requestAnimationFrame(() => {
          if (container) {
            const currentScrollTop = container.scrollTop;
            const currentScrollHeight = container.scrollHeight;
            const currentClientHeight = container.clientHeight;
            const distanceFromBottom = currentScrollHeight - currentScrollTop - currentClientHeight;
            // 如果距离底部超过 10px，再次滚动到底部
            if (distanceFromBottom > 10) {
              container.scrollTop = currentScrollHeight - currentClientHeight;
            }
          }
        });
      });
    }
  };

  render() {
    const { ListHeaderComponent, ListFooterComponent, data, keyExtractor } = this.props;
    // Messages array is in chronological order (oldest first, newest last)
    // For web display, render directly without reversing
    const displayData = data;
    return (
      <div
        ref={this.setContainerRef}
        onScroll={this.handleScroll}
        onWheel={this.props.onUserScroll}
        onTouchStart={this.props.onUserScroll}
        onPointerDown={this.props.onUserScroll}
        style={styles.container}
      >
        <ListHeaderComponent />
        {displayData.map((item, index) => {
          const originalIndex = index;
          let key;
          if (keyExtractor) {
            key = keyExtractor(item, originalIndex);
          } else if (item && item.id !== undefined && item.id !== null) {
            key = `msg-${item.id}`;
          } else {
            key = `msg-index-${originalIndex}`;
          }
          return (
            <div key={key} data-message-id={item?.id}>
              {this.renderItem(item, originalIndex)}
            </div>
          );
        })}
        <ListFooterComponent />
      </div>
    );
  }
}

const styles = {
  container: {
    height: '100%',
    minHeight: '100%',
    width: '100%',
    maxWidth: '100%',
    overflowX: 'hidden',
    overflowY: 'auto',
    display: 'flex',
    flexDirection: 'column',
    flex: 1,
    alignItems: 'stretch',
    boxSizing: 'border-box'
  }
};

WebScrollView.defaultProps = {
  data: [],
  extraData: {},
  ListHeaderComponent: () => {},
  ListFooterComponent: () => {},
  inverted: false,
  shouldAutoScrollToBottom: true,
  keyExtractor: (item, index) => item?.id?.toString() || index.toString()
};

WebScrollView.propTypes = {
  data: PropTypes.arrayOf(PropTypes.object),
  extraData: PropTypes.object,
  inverted: PropTypes.bool,
  renderFooter: PropTypes.func,
  keyExtractor: PropTypes.func,
  enableEmptySections: PropTypes.bool,
  automaticallyAdjustContentInsets: PropTypes.bool,
  contentContainerStyle: PropTypes.object,
  renderItem: PropTypes.func,
  ListHeaderComponent: PropTypes.func,
  ListFooterComponent: PropTypes.func,
  onScroll: PropTypes.func,
  shouldAutoScrollToBottom: PropTypes.bool,
  onUserScroll: PropTypes.func,
  scrollContainerRef: PropTypes.shape({ current: PropTypes.any })
};
